import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { ChipModule } from 'primeng/chip';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { StyleClassModule } from 'primeng/styleclass';
import { CheckboxModule } from 'primeng/checkbox';

import { Document } from '../../../models/document';
import { Tag } from '../../../models/tag';
import { DocumentService } from '../../../core/services/api/document.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ToastService } from '../../../core/services/toast.service';

@Component({
  selector: 'app-kb-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    AutoCompleteModule,
    ChipModule,
    ButtonModule,
    MessageModule,
    IconFieldModule,
    InputIconModule,
    StyleClassModule,
    CheckboxModule,
    TranslocoPipe,
  ],
  templateUrl: './kb-form.component.html',
  styleUrls: ['./kb-form.component.scss'],
})
export class KbFormComponent implements OnInit {
  kbForm: FormGroup;
  isEditMode = false;
  isSubmitting = false;
  selectedTags: Tag[] = [];
  availableTags: Tag[] = [];
  filteredTags: Tag[] = [];
  selectedFile: File | null = null;
  filePreview: string | null = null;
  originalFile: string | null = null; // Track original file path/URL for edit mode
  originalTitle: string | null = null; // Track original title for edit mode
  originalIsSensitive: boolean | null = null; // Track original is_sensitive value for edit mode
  maxFileSize = 50000 * 1024 * 1024; // 50000MB
  allowedFileTypes = [
    'application/pdf',
    'text/plain',
    'text/markdown',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/csv',
  ];

  // Document type options
  documentTypeOptions = [
    { label: 'kbForm.types.pdf', value: 'pdf' },
    { label: 'kbForm.types.text', value: 'text' },
    { label: 'kbForm.types.word', value: 'word' },
    { label: 'kbForm.types.csv', value: 'csv' },
    { label: 'kbForm.types.other', value: 'other' },
  ];

  private fb = inject(FormBuilder);
  public ref = inject(DynamicDialogRef);
  public config = inject(DynamicDialogConfig);
  private cdr = inject(ChangeDetectorRef);
  private translocoService = inject(TranslocoService);
  private documentService = inject(DocumentService);
  private toastService = inject(ToastService);

  constructor() {
    this.maximizeDialog();
    this.kbForm = this.createForm();
  }

  maximizeDialog() {
    const maximizeButton = document.querySelector('.p-dialog .p-dialog-maximize-button');
    (maximizeButton as HTMLElement)?.click();
  }

  async ngOnInit() {
    // Setup browser back button handling
    this.setupBrowserBackButtonHandler();

    // Load available tags
    await this.loadTags();

    // Check if we're in edit mode and populate form
    const documentData = this.config.data?.document;
    this.isEditMode = this.config.data?.mode === 'edit';
    if (documentData) {
      this.populateForm(documentData);
    }
  }

  createForm(): FormGroup {
    return this.fb.group({
      title: ['', [Validators.required]],
      // description: [''],
      type: ['pdf', [Validators.required]],
      text: [''],
      // tags: [[]],
      // tagInputControl: [''],
      file: [null, [Validators.required]],
      is_sensitive: [false],
    });
  }

  populateForm(document: Document) {
    this.kbForm.patchValue({
      title: document.title || '',
      // description: document.text || '',
      type: document.type || 'pdf',
      file: document.file || null,
      is_sensitive: document.is_sensitive || false,
    });

    // Store original values for change detection
    this.originalTitle = document.title || '';
    this.originalIsSensitive = document.is_sensitive || false;

    if (document.tag_name_color_pairs && document.tag_name_color_pairs.length > 0) {
      this.selectedTags = [...document.tag_name_color_pairs];
      this.kbForm.patchValue({ tags: this.selectedTags });
    }

    if (document.file) {
      this.filePreview = document.file;
      this.originalFile = document.file; // Store original file reference
    }
  }

  // Load available tags (mock data for now)
  async loadTags() {
    // This would typically come from a service
    this.availableTags = [
      { uuid: '1', name: 'Important', color: '#ef4444' },
      { uuid: '2', name: 'Reference', color: '#3b82f6' },
      { uuid: '3', name: 'Tutorial', color: '#10b981' },
      { uuid: '4', name: 'Documentation', color: '#f59e0b' },
      { uuid: '5', name: 'Guide', color: '#8b5cf6' },
    ];
  }

  // Tags methods
  filterTags(event: any) {
    const query = event.query.toLowerCase();
    this.filteredTags = this.availableTags.filter((tag) => tag.name.toLowerCase().includes(query));
  }

  onTagSelect(event: any) {
    const tag = event.value;
    if (tag && !this.selectedTags.find((t) => t.name === tag.name)) {
      this.selectedTags.push(tag);
      this.kbForm.patchValue({ tags: this.selectedTags });
    }
    this.kbForm.get('tagInputControl')?.setValue('');
  }

  addTag(event: any) {
    if (event.key === 'Enter') {
      const value = this.kbForm.get('tagInputControl')?.value;
      const trimmedTag = value?.trim();
      if (trimmedTag && !this.selectedTags.find((t) => t.name === trimmedTag)) {
        const newTag: Tag = { uuid: Date.now().toString(), name: trimmedTag, color: '#6b7280' };
        this.selectedTags.push(newTag);
        this.kbForm.patchValue({ tags: this.selectedTags });
      }
      this.kbForm.get('tagInputControl')?.setValue('');
    }
  }

  removeTag(tag: Tag) {
    this.selectedTags = this.selectedTags.filter((t) => t.name !== tag.name);
    this.kbForm.patchValue({ tags: this.selectedTags });
  }

  // File upload methods
  onFileSelect(event: any) {
    const file = event.target.files[0];
    if (file) {
      // Validate file size first
      if (file.size > this.maxFileSize) {
        this.toastService.error(
          this.translocoService.translate('kbForm.alerts.fileTooLarge'),
          this.translocoService.translate('kbForm.alerts.fileSizeExceeded', {
            maxSize: '50MB',
            fileName: file.name,
          }),
        );
        // Reset the file input
        event.target.value = '';
        return;
      }

      // Validate file type
      if (!this.allowedFileTypes.includes(file.type)) {
        this.toastService.error(
          this.translocoService.translate('kbForm.alerts.invalidFileTypeTitle'),
          this.translocoService.translate('kbForm.alerts.invalidFileType'),
        );
        // Reset the file input
        event.target.value = '';
        return;
      }

      this.selectedFile = file;
      this.kbForm.patchValue({ file: file });

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.filePreview = e.target?.result as string;
          this.cdr.detectChanges();
        };
        reader.readAsDataURL(file);
      } else {
        this.filePreview = null;
      }

      // Auto-detect file type
      this.detectFileType(file);
    }
  }

  detectFileType(file: File) {
    if (file.type === 'application/pdf') {
      this.kbForm.patchValue({ type: 'pdf' });
    } else if (file.type === 'text/plain') {
      this.kbForm.patchValue({ type: 'text' });
    } else if (file.type === 'text/markdown') {
      this.kbForm.patchValue({ type: 'text' });
    } else if (file.type.includes('word')) {
      this.kbForm.patchValue({ type: 'word' });
    } else {
      this.kbForm.patchValue({ type: 'other' });
    }
  }

  removeFile() {
    this.selectedFile = null;
    this.filePreview = null;
    // Don't clear originalFile in edit mode - we need it to detect if file was removed
    // Only clear it in create mode
    // if (!this.isEditMode) {
    this.originalFile = null;
    // }
    this.kbForm.patchValue({ file: null });

    // Reset the file input
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  hasFileChanged(): boolean {
    if (!this.isEditMode) {
      return this.selectedFile !== null; // In create mode, any file is a change
    }

    return this.selectedFile !== null;
  }

  hasFileBeenRemoved(): boolean {
    return this.isEditMode && !this.originalFile && !this.selectedFile;
  }

  isFileUnchanged(): boolean {
    return this.isEditMode && this.originalFile && !this.selectedFile;
  }

  hasTitleChanged(): boolean {
    if (!this.isEditMode) {
      return true; // In create mode, always consider it changed
    }
    const currentTitle = this.kbForm.get('title')?.value || '';
    return currentTitle !== this.originalTitle;
  }

  hasSensitiveChanged(): boolean {
    if (!this.isEditMode) {
      return true; // In create mode, always consider it changed
    }
    const currentIsSensitive = this.kbForm.get('is_sensitive')?.value || false;
    return currentIsSensitive !== this.originalIsSensitive;
  }

  hasFormChanged(): boolean {
    if (!this.isEditMode) {
      return true; // In create mode, always consider it changed
    }
    // Check if title changed
    if (this.hasTitleChanged()) {
      return true;
    }
    // Check if file changed
    if (this.hasFileChanged()) {
      return true;
    }
    // Check if file was removed
    if (this.hasFileBeenRemoved()) {
      return true;
    }
    // Check if is_sensitive changed
    if (this.hasSensitiveChanged()) {
      return true;
    }
    return false;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  // Getter for save button disabled state
  get isSaveDisabled(): boolean {
    if (!this.kbForm.valid || this.isSubmitting) {
      return true;
    }

    // In edit mode, disable until user makes a change
    if (this.isEditMode) {
      return !this.hasFormChanged() || (!this.kbForm.valid);
    }

    // In create mode, require a file to be selected
    return !this.selectedFile;
  }

  // Form submission
  async onSubmit() {
    if (this.kbForm.valid && !this.isSubmitting) {
      this.isSubmitting = true;
      const formValue = this.kbForm.value;

      try {
        let response;

        if (this.isEditMode && this.config.data?.document?.uuid) {
          // EDIT MODE LOGIC
          // Start with original document data
          const formData = new FormData();
          const documentData: Document = { ...this.config.data.document };

          // Only update fields that have changed
          if (this.hasTitleChanged()) {
            formData.append('title', formValue.title);
          }

          // Update is_sensitive field
          formData.append('is_sensitive', formValue.is_sensitive ? 'true' : 'false');

          // Only update type if file changed (type is auto-detected from file)
          if (this.hasFileChanged() && this.selectedFile) {
            formData.append('file', this.selectedFile);
            formData.append('type', formValue.type);
          }
          response = await this.documentService.updateDocument(
            this.config.data.document.uuid,
            formData,
          );

        } else {
          // CREATE MODE LOGIC
          if (this.selectedFile) {
            // Create with file upload
            const formData = new FormData();
            formData.append('file', this.selectedFile);
            formData.append('title', formValue.title);
            formData.append('type', formValue.type);
            formData.append('is_sensitive', formValue.is_sensitive ? 'true' : 'false');
            // formData.append('text', formValue.description || '');
            // formData.append('tags', JSON.stringify(this.selectedTags));

            response = await this.documentService.createDocuments(formData);
          }
        }

        this.ref.close({ success: true, data: response });
      } catch (error) {
        console.error('Error saving document:', error);
        this.ref.close({ success: false, error: error });
      }
    } else {
      // Mark all fields as touched to show validation errors
      this.kbForm.markAllAsTouched();
    }
    this.isSubmitting = false;
  }

  ngOnDestroy() {
    this.removeBrowserBackButtonHandler();
  }

  // Browser back button handling
  private popStateHandler = () => {
    this.onCancel();
  };

  private setupBrowserBackButtonHandler() {
    // Push a state to the history to detect back button press
    history.pushState({ dialogOpen: true }, '');
    window.addEventListener('popstate', this.popStateHandler);
  }

  private removeBrowserBackButtonHandler() {
    window.removeEventListener('popstate', this.popStateHandler);
  }

  onCancel() {
    this.ref.close();
  }
}
